import { InputField } from "@/components/input-field/InputField";
import { AsyncAutocompleteSelect, Option } from "@/components/select/AsyncAutocompleteSelect";
import { AutocompleteSelect } from "@/components/select/AutocompleteSelect";
import { SelectComponent } from "@/components/select/Select";
import { AppColors } from "@/styles/colors";
import { Card, Field, Grid, GridItem, Text } from "@chakra-ui/react";
import { HADifferenceDisplay } from "./HADifferenceDisplay";
import { FieldErrors, Control } from "react-hook-form";
import { ProductPlanificationCreationForm } from "../../schema/product-planification.schema";
import { handleTonChangeInInput } from "@/utils/query-string";
import { numbersWithCommaDecimal } from "@/utils/reg-expressions";
import { HADifferenceItem } from "../../create/HADifferenceModal";
import { Dispatch, SetStateAction } from "react";

interface Props {
  errors: FieldErrors<ProductPlanificationCreationForm>;
  control: Control<ProductPlanificationCreationForm>;
  loadAccountOptions: (inputValue: string) => Promise<Option[]>
  loadingOptions: boolean;
  zonaOptions: Option[];
  loadingZonas: boolean;
  loadingAccount: boolean;
  loadingCreate: boolean;
  loadingCuentaUsuarios: boolean;
  watched: any;
  cuentaUsuarios: any[];
  campanaOptions: Option[];
  showHADifferenceButton: boolean
  haDifferenceMotivosValid: boolean
  haDifferenceAbs: number
  motivosDesvio: HADifferenceItem[]
  setOpenHADifferenceModal: Dispatch<SetStateAction<boolean>>
  readOnlyMainFields?: boolean;
}

export const PPMainDataForm = ({
  errors,
  control,
  watched,
  cuentaUsuarios,
  loadingAccount,
  loadAccountOptions,
  loadingCreate,
  loadingCuentaUsuarios,
  loadingOptions,
  campanaOptions,
  zonaOptions,
  loadingZonas,
  showHADifferenceButton,
  haDifferenceMotivosValid,
  haDifferenceAbs,
  motivosDesvio,
  setOpenHADifferenceModal,
  readOnlyMainFields,
}: Props) => {
  const mainDisabled = Boolean(readOnlyMainFields) || loadingCreate;
  return (
    <Card.Root maxW="full" borderRadius="8px">
      <Card.Body
        paddingX={{ base: "20px", md: "32px", xl: "48px" }}
        paddingY="30px"
        borderRadius="8px"
        border={`1px solid ${AppColors.WHITE_BORDER_1}`}
      >
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)", xl: "repeat(4, 1fr)" }}
          gap="16px"
          alignItems="end"
        >
          <GridItem colSpan={1} minW={0}>
            <Field.Root invalid={!!(errors as any)?.account?.message} gap="8px">
              <Field.Label>
                <Text fontSize="16px" fontWeight={600}>
                  Cuenta{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </Text>
              </Field.Label>
              <AsyncAutocompleteSelect
                control={control}
                loadOptions={loadAccountOptions}
                name="account"
                placeholder="Buscar..."
                isLoading={loadingAccount}
                disabled={mainDisabled}
              />
              <Field.ErrorText>
                {typeof (errors as any)?.account?.message === "string"
                  ? (errors as any).account.message
                  : ""}
              </Field.ErrorText>
            </Field.Root>
          </GridItem>

          <GridItem colSpan={1} minW={0}>
            <Field.Root
              invalid={!!(errors as any)?.idUsuarioMobile?.message}
              gap="8px"
            >
              <Field.Label>
                <Text fontSize="16px" fontWeight={600}>
                  Usuario mobile
                </Text>
              </Field.Label>
              <AutocompleteSelect
                control={control as any}
                inputName="idUsuarioMobile"
                loading={loadingCuentaUsuarios}
                disabled={mainDisabled || !watched?.account?.value || loadingCuentaUsuarios}
                multiple={false}
                items={
                  cuentaUsuarios?.map((cu: any) => ({
                    label: `${cu?.nombre} ${cu?.apellido}`,
                    value: cu?.id,
                  })) ?? []
                }
              />
              <Field.ErrorText>
                {typeof (errors as any)?.idUsuarioMobile?.message === "string"
                  ? (errors as any).idUsuarioMobile.message
                  : ""}
              </Field.ErrorText>
            </Field.Root>
          </GridItem>

          <GridItem colSpan={1} minW={0}>
            <SelectComponent
              fullWidth
              items={campanaOptions}
              menuMaxHeight="250px"
              name="idCampana"
              control={control as any}
              closeOnSelect
              clearable
              size="xs"
              minHeight="40px"
              labelText="Campaña"
              placeholder="Seleccionar"
              loading={loadingOptions}
              disabled={mainDisabled || loadingOptions}
              required
            />
          </GridItem>

          <GridItem colSpan={1} minW={0}>
            <SelectComponent
              fullWidth
              items={zonaOptions}
              menuMaxHeight="250px"
              name="idZonaProductiva"
              control={control as any}
              closeOnSelect
              clearable
              size="xs"
              minHeight="40px"
              labelText="Zona productiva"
              placeholder="Seleccionar"
              loading={loadingZonas}
              disabled={mainDisabled || !watched?.account?.value || loadingZonas}
              required
            />
          </GridItem>

          <GridItem colSpan={1} minW={0}>
            <InputField
              control={control as any}
              inputName="haPropias"
              labelText="HA Propias"
              placeholder="0"
              minHeight="40px"
              disabled={mainDisabled}
              required
              type="text"
              allowedPattern={numbersWithCommaDecimal}
              onChange={handleTonChangeInInput}
            />
          </GridItem>

          <GridItem colSpan={1} minW={0}>
            <InputField
              control={control as any}
              inputName="haAlquiladas"
              labelText="HA Alquiladas"
              placeholder="0"
              minHeight="40px"
              disabled={mainDisabled}
              type="text"
              required
              allowedPattern={numbersWithCommaDecimal}
              onChange={handleTonChangeInInput}
            />
          </GridItem>
        </Grid>

        {showHADifferenceButton && (
          <HADifferenceDisplay
            haDifferenceMotivosValid={haDifferenceMotivosValid}
            motivosDesvio={motivosDesvio}
            setOpenHADifferenceModal={setOpenHADifferenceModal}
            loadingCreate={loadingCreate}
            haDifferenceAbs={haDifferenceAbs}
          />
        )}
      </Card.Body>
    </Card.Root>
  );
};
